import { Injectable } from '@angular/core';
import { Config, DriveStep, DriverHook } from 'driver.js';
import { TranslateService } from '@ngx-translate/core';

import { ForceCloseGuideParamsType, StartBidsGuideParamsType } from '../types/bids-guide.types';
import { GuideDriverAdapterService } from '../../../shared-components/guide/core/services/guide-driver-adapter.service';
import { AbstractGuideService } from '../../../shared-components/guide/core/services/abstract-guide.service';

@Injectable()
export class BidsGuideService extends AbstractGuideService {
  constructor(
    guideDriverAdapterService: GuideDriverAdapterService,
    translateService: TranslateService,
  ) {
    super(guideDriverAdapterService, translateService);
  }

  start(params: StartBidsGuideParamsType): void {
    const config: Config = {
      showProgress: true,
      animate: true,
      allowKeyboardControl: true,
      allowClose: true,
      prevBtnText: this.translate('DRIVER_CONFIG.PREV_BTN'),
      nextBtnText: this.translate('DRIVER_CONFIG.NEXT_BTN'),
      doneBtnText: this.translate('DRIVER_CONFIG.DONE_BTN'),
      onDestroyed: this.createCleanupHook(params),
      steps: this.buildSteps(params),
    };

    this.startGuide(config);
  }

  forceClose(params?: ForceCloseGuideParamsType): void {
    this.destroyGuideSafely();
    params?.hideDemoRow();
  }

  private createCleanupHook(params: StartBidsGuideParamsType): DriverHook {
    return () => {
      params.hideDemoRow();
    };
  }

  private buildSteps(params: StartBidsGuideParamsType): DriveStep[] {
    return [
      {
        element: this.buildAnchorSelector('title'),
        popover: this.buildPopover({
          titleKey: 'LOT_INFO.GUIDE.MY_BIDS.TITLE',
          descriptionKey: 'LOT_INFO.GUIDE.MY_BIDS.DESCR',
        }),
      },
      {
        element: this.buildAnchorSelector('filter'),
        popover: this.buildPopover({
          titleKey: 'LOT_INFO.GUIDE.FILTER.TITLE',
          descriptionKey: 'LOT_INFO.GUIDE.FILTER.DESCR',
        }),
      },
      {
        element: this.buildAnchorSelector('filter-status'),
        popover: this.buildPopover({
          titleKey: 'LOT_INFO.GUIDE.STATUS.TITLE',
          descriptionKey: 'LOT_INFO.GUIDE.STATUS.DESCR',
          overrides: {
            onNextClick: () => {
              const driver = this.guideDriverAdapterService.getDriverInstance();

              if (!params.hasRealBids()) {
                params.showDemoRow();
                driver.refresh();
              }

              driver.moveNext();
            },
          },
        }),
      },
      {
        element: this.buildAnchorSelector('table'),
        popover: this.buildPopover({
          titleKey: 'LOT_INFO.GUIDE.BIDS_LIST.TITLE',
          descriptionKey: 'LOT_INFO.GUIDE.BIDS_LIST.DESCR',
          overrides: {
            side: 'top',
            align: 'center',
          },
        }),
      },
      {
        element: this.buildAnchorSelector('comment-button'),
        popover: this.buildPopover({
          titleKey: 'LOT_INFO.GUIDE.COMMENT.TITLE',
          descriptionKey: 'LOT_INFO.GUIDE.COMMENT.DESCR',
        }),
        onHighlighted: (element) => {
          element?.classList?.add('guide-pulse-primary');
        },
        onDeselected: (element) => {
          element?.classList?.remove('guide-pulse-primary');
        },
      },
      {
        element: this.buildAnchorSelector('delete-button'),
        popover: this.buildPopover({
          titleKey: 'LOT_INFO.GUIDE.DELETE_BID.TITLE',
          descriptionKey: 'LOT_INFO.GUIDE.DELETE_BID.DESCR',
        }),
        onHighlighted: (element) => {
          element?.classList?.add('guide-pulse-danger');
        },
        onDeselected: (element) => {
          element?.classList?.remove('guide-pulse-danger');
        },
      },
      {
        popover: this.buildPopover({
          titleKey: 'LOT_INFO.GUIDE.FINISH.TITLE',
          descriptionKey: 'LOT_INFO.GUIDE.FINISH.DESCR',
        }),
      },
    ];
  }
}
